import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import LinearProgress from '@mui/material/LinearProgress';
import Error from '@src/app/components/Error';
import './Login.scss';
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { getFirestore, getDoc, setDoc, deleteDoc, doc } from 'firebase/firestore';
import { signInAuth, logError } from '@actions/userActions';
import { serializeForRedux } from '@src/utilities/serializeForRedux';
import OtpInput from 'react-otp-input';

const Login = () => {
    const navigate = useNavigate();
    const auth = getAuth();
    const [params] = useSearchParams();
    const dispatch = useDispatch();
    const [phoneNumber, setPhoneNumber] = useState('');
    const [recaptchaVerifier, setRecaptchaVerifier] = useState<any>(null);
    const [errPhone, setErrPhone] = useState('');
    const [errOTP, setErrOTP] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [confirmationResult, setConfirmationResult]: any = useState('');

    useEffect(() => {
        const userData = JSON.parse(localStorage?.getItem('userData') || '{}');
        if (userData?.phoneNumber) {
            navigate('/');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
    }, []);

    useEffect(() => {
        auth.languageCode = 'en';
        const recaptchaVerifierData = new RecaptchaVerifier('sign-in-button', {
            size: 'invisible',
            callback: (_response: any) => {},
        }, auth);
        setRecaptchaVerifier(recaptchaVerifierData);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
    }, []);

    const handleOTPChange = (otp: string) => {
        setVerificationCode(otp);
        if (otp.length === 6) {
            setTimeout(() => {
                if (otp.length === 6) {
                    handleVerifyCode(null, navigate, otp);
                }
            }, 300);
        }
    };

    const handleVerifyCode = async (event: any, navigateFn: any, otp: any) => {
        if (event) {
            event.preventDefault();
        }

        if (otp.length !== 6) {
            setErrOTP('Please enter a 6-digit OTP');
            return;
        }

        setIsLoading(true);

        confirmationResult.confirm(otp).then(async (result: any) => {
            const db = getFirestore();
            const user = result.user;

            if (user?.uid && user?.phoneNumber) {
                try {
                    const userPhone = user?.phoneNumber;
                    const tempUserRef = doc(db, 'users', userPhone);
                    const tempUserSnap = await getDoc(tempUserRef);
                    if (tempUserSnap.exists()) {
                        const userData = tempUserSnap.data();
                        const newUserRef = doc(db, 'users', user?.uid);
                        await setDoc(newUserRef, { ...userData, isTemp: false });
                        await deleteDoc(tempUserRef);
                        console.log(`User ${userPhone} data migrated to UID: ${user.uid}`);
                    }
                    const userRef = doc(db, 'users', user?.uid);
                    const userSnap = await getDoc(userRef);
                    if (userSnap.exists()) {
                        const data: any = userSnap.data() || {};
                        dispatch(signInAuth({
                            uid: user?.uid,
                            name: user?.displayName,
                            email: user?.email,
                            accessToken: user?.accessToken,
                            phoneNumber: user?.phoneNumber,
                            data: serializeForRedux(data),
                        }));
                        if (localStorage?.getItem('REACT_APP_FORCE_ACTIONS') !== process.env.REACT_APP_FORCE_ACTIONS) {
                            localStorage?.setItem('REACT_APP_FORCE_ACTIONS', process.env.REACT_APP_FORCE_ACTIONS || '');
                        }
                        localStorage?.setItem('REACT_APP_VERSION', process.env.REACT_APP_VERSION || '');

                        if (data?.roles) {
                            navigateFn('/admin');
                        } else if (params.get('redirect')) {
                            navigateFn(params.get('redirect'));
                        } else {
                            navigateFn('/');
                        }
                        setIsLoading(false);
                    } else {
                        console.log('User not pre-registered. Connect with Admin.');
                        navigateFn('/');
                    }
                } catch (e) {
                    console.error('Error fetching document: ', e);
                }
            }
        }).catch((error: any) => {
            dispatch(logError({ severity: 'error', message: error.message }));
            const errorMsg = error?.message?.includes('invalid-verification-code')
                ? 'Invalid OTP. Please try again.'
                : error?.message?.split('(')[0].split(':')[1] || 'Verification failed. Please try again.';
            setErrOTP(errorMsg);
            setIsLoading(false);
        });
    };

    const handleSendCode = async (_event: any) => {
        setErrPhone('');

        if (!phoneNumber) {
            setErrPhone('Please enter your phone number');
            return;
        }

        if (phoneNumber.length !== 10) {
            setErrPhone('Please enter a valid 10-digit phone number');
            return;
        }

        if (!/^[6-9]\d{9}$/.test(phoneNumber)) {
            setErrPhone('Please enter a valid Indian phone number');
            return;
        }

        setIsLoading(true);

        signInWithPhoneNumber(auth, '+91' + phoneNumber, recaptchaVerifier)
            .then((result) => {
                setIsLoading(false);
                setConfirmationResult(result);
            }).catch((error) => {
                setIsLoading(false);

                let errorMessage = '';
                console.log('err--', error);
                switch (error.code) {
                    case 'auth/billing-not-enabled':
                    case 'auth/invalid-phone-number':
                        errorMessage = 'Invalid phone number';
                        break;
                    case 'auth/too-many-requests':
                        errorMessage = 'Too many attempts. Please try again later';
                        break;
                    case 'auth/quota-exceeded':
                        errorMessage = 'Service temporarily unavailable. Please try again later';
                        break;
                    case 'auth/invalid-app-credential':
                        errorMessage = 'Invalid App Credential';
                        break;
                    default:
                        errorMessage = error?.message?.split('(')[0].split(':')[1] || 'Something went wrong. Please try again';
                }

                setErrPhone(errorMessage);
            });
    };

    const handleSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        handleSendCode(event);
    };

    const resetPhoneStep = () => {
        setConfirmationResult('');
        setVerificationCode('');
        setErrOTP('');
    };

    return (
        <div className="login-page">
            <div className="login-card">
                <header className="login-card__header">
                    <h1 className="login-card__title">
                        {confirmationResult ? 'Enter verification code' : 'Welcome back'}
                    </h1>
                    <p className="login-card__subtitle">
                        {confirmationResult
                            ? 'We sent a 6-digit OTP to your phone'
                            : 'Sign in with your registered mobile number'}
                    </p>
                    {confirmationResult && (
                        <span className="login-card__phone-display">+91 {phoneNumber}</span>
                    )}
                </header>

                {!confirmationResult && (
                    <form className="login-form" onSubmit={handleSubmitForm}>
                        <div className="login-field">
                            <label className="login-field__label" htmlFor="phone">Phone number</label>
                            <div className="login-field__input-wrap">
                                <span className="login-field__prefix">+91</span>
                                <input
                                    id="phone"
                                    className="login-field__input"
                                    type="tel"
                                    inputMode="numeric"
                                    autoComplete="tel"
                                    autoFocus
                                    disabled={isLoading}
                                    maxLength={10}
                                    placeholder="10-digit mobile number"
                                    value={phoneNumber}
                                    onChange={(event) => setPhoneNumber(event.target.value.replace(/\D/g, ''))}
                                />
                            </div>
                        </div>

                        {isLoading && <LinearProgress className="login-progress" />}

                        <button
                            type="submit"
                            className="login-btn login-btn--primary"
                            disabled={isLoading}
                        >
                            Send OTP
                        </button>

                        <Error message={errPhone || ''} />
                    </form>
                )}

                {confirmationResult && (
                    <form
                        className="login-form"
                        onSubmit={(event) => handleVerifyCode(event, navigate, verificationCode)}
                    >
                        <p className="login-otp-label">One-time password</p>
                        <OtpInput
                            value={verificationCode}
                            onChange={handleOTPChange}
                            numInputs={6}
                            renderInput={(props) => (
                                <input
                                    {...props}
                                    className="otp-input"
                                    type="number"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={1}
                                    pattern="\d*"
                                />
                            )}
                            shouldAutoFocus
                            containerStyle="otp-container"
                            skipDefaultStyles
                        />

                        {isLoading && <LinearProgress className="login-progress" />}

                        <Error message={errOTP || ''} />

                        <button
                            type="button"
                            className="login-btn login-btn--ghost"
                            disabled={isLoading}
                            onClick={resetPhoneStep}
                        >
                            Change phone number
                        </button>
                    </form>
                )}

                <footer className="login-footer">
                    <Link to="/">← Back to home</Link>
                </footer>
            </div>

            <div id="sign-in-button" />
        </div>
    );
};

export default Login;
